import { useEffect, useState } from 'react'
import * as idp from './idpClient'
import type { FederatedProvider } from './types'

/**
 * The sign-in choices the page can put in front of someone.
 *
 * They arrive from two endpoints in the same shape: `GET /auth/provider` names
 * the platform's own registrations, which need no email address, and
 * `POST /auth/discovery` names the connection an address belongs to. Both are
 * turned into the same option here, so starting one is a single code path.
 */

export interface SignInOption {
  provider: FederatedProvider
  /** Text for the button. */
  label: string
  /** Targets a customer's own registration; absent for a platform default. */
  connectionId?: string
}

const fallbackLabels: Record<FederatedProvider, string> = {
  microsoft: 'Microsoft',
  google: 'Google',
}

/**
 * Whether the portal can carry a sign-in with this provider through to the end.
 *
 * The IdP reports provider names as free-form path segments, so a deployment
 * may name one this build has no callback handling for. Offering it would
 * strand the person at `/callback`, so an unrecognised name is not offered.
 */
const isFederatedProvider = (value: string): value is FederatedProvider =>
  value === 'microsoft' || value === 'google'

/** Turn one wire entry into an option, or nothing if it cannot be offered. */
export const toSignInOption = (entry: {
  provider: string
  displayName?: string | null
  connectionId?: string | null
}): SignInOption | undefined => {
  if (!isFederatedProvider(entry.provider)) return undefined

  return {
    provider: entry.provider,
    label: entry.displayName ?? fallbackLabels[entry.provider],
    ...(entry.connectionId ? { connectionId: entry.connectionId } : {}),
  }
}

export const toSignInOptions = (
  entries: { provider: string; displayName?: string | null; connectionId?: string | null }[]
): SignInOption[] => entries.flatMap(entry => toSignInOption(entry) ?? [])

/**
 * The platform providers this deployment offers, for the buttons shown before
 * anyone has typed an address.
 *
 * Three things can leave the list empty: the deployment names no provider, it
 * names some but none is fully configured, or the service cannot be reached.
 * The page treats all three the same — there is nothing to click, and nothing
 * the person can do about it — so they collapse to one empty result rather than
 * three states with three messages.
 */
export const useSignInOptions = (): { options: SignInOption[]; isLoading: boolean } => {
  const [options, setOptions] = useState<SignInOption[]>([])
  const [isLoading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const { providers } = await idp.listAuthProviders()
        if (cancelled) return
        setOptions(toSignInOptions(providers.filter(entry => entry.enabled)))
      } catch (cause) {
        if (cancelled) return
        // Worth recording, but the page has the same nothing to show either way.
        console.error('Could not list the sign-in providers', cause)
        setOptions([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [])

  return { options, isLoading }
}
